import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Clock, Leaf, Drumstick, Heart, Eye, Sparkles } from 'lucide-react';
import { getFallbackSvg } from './Outletfallbacks';
import './style/outlet.css';

// ─── Time helpers ────────────────────────────────────────────────────────
//
// opensAt / CloseAt are stored as ms-since-midnight (see timeToMs in
// AddProperty.jsx). Keep minute precision here — don't truncate to hours,
// or a cafe closing at 05:30 will incorrectly read as closing at 05:00.

const msToHM = (ms) => {
  const totalMinutes = Math.floor(ms / 60000);
  return {
    h: Math.floor(totalMinutes / 60),
    m: totalMinutes % 60,
  };
};

const formatTime = (ms) => {
  if (ms == null) return '—';
  const { h, m } = msToHM(ms);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h % 12) || 12);
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
};

/** Current time as ms-since-midnight, using the browser's local clock —
 *  matches how opensAt/CloseAt are entered & displayed to the user. */
const getCurrentMsSinceMidnight = () => {
  const now = new Date();
  return (
    now.getHours()   * 3600000 +
    now.getMinutes()  * 60000  +
    now.getSeconds()  * 1000   +
    now.getMilliseconds()
  );
};

/**
 * Handles both same-day hours (opensAt < closeAt) and overnight hours
 * that wrap past midnight (opensAt > closeAt, e.g. 17:00 → 05:00).
 * Mirrors the $expr logic used in the backend's findNearbyOutlets query.
 */
const isCurrentlyOpen = (opensAt, closeAt, curMs) => {
  if (opensAt == null || closeAt == null) return false;

  if (opensAt <= closeAt) {
    // same-day
    return curMs >= opensAt && curMs < closeAt;
  }
  // overnight — open if we're still "today" past opening,
  // or already past midnight but before tomorrow's closing
  return curMs >= opensAt || curMs < closeAt;
};

const formatTag = str =>
  str.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const Outlet = ({ outlet, setIsSingleItem }) => {
  const [curMs, setCurMs] = useState(getCurrentMsSinceMidnight());
  const navigate = useNavigate();

  useEffect(() => {
    const tick = () => setCurMs(getCurrentMsSinceMidnight());
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, []);

  const doc      = outlet;
  const distance = parseFloat(outlet.distance).toFixed(1);

  const open   = doc.opensAt;
  const close  = doc.CloseAt;
  // The server also honours the partner's "closed" switch; fall back to hours for older payloads
  const isOpen = typeof doc.openNow === 'boolean' ? doc.openNow : isCurrentlyOpen(open, close, curMs);
  const reason = doc.match?.reason;
  const isVeg  = doc.menuType?.toLowerCase() === 'veg';

  const cuisines  = doc.cousine  || [];
  const ambiences = doc.ambience || [];
  const vibe      = (doc.vibe    || [])[0] ?? null;

  const metaParts = [
    doc.category,
    ...ambiences.slice(0, 2).map(formatTag),
  ].filter(Boolean);

  const fallbackSrc = getFallbackSvg(doc._id);

  const { displayLikes, displayViews } = useMemo(() => {
    const views = doc.views && doc.views > 0
      ? doc.views
      : Math.floor(Math.random() * 900 + 100);
    const likes = doc.likes && doc.likes > 0
      ? doc.likes
      : Math.floor(Math.random() * (views * 0.49));
    return { displayLikes: likes, displayViews: views };
  }, [doc._id, doc.likes, doc.views]);

  const handleNavigate = useCallback(() => {
    setIsSingleItem(true);
    navigate(`/moredetails?cafeid=${doc._id}`);
  }, [doc._id, navigate, setIsSingleItem]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleNavigate();
    }
  }, [handleNavigate]);

  return (
    <article
      className="ot-card"
      onClick={handleNavigate}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${doc.name}`}
    >
      {/* ── Image ─────────────────────────────── */}
      <div className="ot-image-wrap">
        {(doc.thumbnail && doc.thumbnail.includes('restropilots.s3')) ? (
          <img src={doc.thumbnail} alt={doc.name} className="ot-image" loading="lazy" />
        ) : (
          <img
            src={fallbackSrc}
            alt={doc.name}
            className="ot-image ot-image--fallback"
            loading="lazy"
            aria-hidden="true"
          />
        )}

        <div className="ot-badges">
          <span className={`ot-badge ${isOpen ? 'ot-badge--open' : 'ot-badge--closed'}`}>
            <span className="ot-dot" />
            {isOpen ? 'Open' : 'Closed'}
          </span>
          <span className={`ot-badge ${isVeg ? 'ot-badge--veg' : 'ot-badge--nonveg'}`}>
            {isVeg ? <Leaf size={11} /> : <Drumstick size={11} />}
            {isVeg ? 'Veg' : 'Non-veg'}
          </span>
        </div>

        <span className="ot-distance">
          <MapPin size={11} /> {distance} km
        </span>
      </div>

      {/* ── Body ──────────────────────────────── */}
      <div className="ot-body">

        <div className="ot-body-top">
          <h3 className="ot-name">{doc.name}</h3>
          <div className="ot-hours">
            <Clock size={12} />
            <span>{formatTime(open)} – {formatTime(close)}</span>
          </div>
        </div>

        {reason && (
          <div className="ot-reason" title={reason}>
            <Sparkles size={12} aria-hidden="true" />
            <span>{reason}</span>
          </div>
        )}

        {metaParts.length > 0 && (
          <p className="ot-meta">{metaParts.join(' · ')}</p>
        )}

        {cuisines.length > 0 && (
          <div className="ot-tags">
            {cuisines.map(c => (
              <span key={c} className="ot-tag">{formatTag(c)}</span>
            ))}
          </div>
        )}

        {ambiences.length > 0 && (
          <div className="ot-tags">
            {ambiences.map(a => (
              <span key={a} className="ot-tag">{formatTag(a)}</span>
            ))}
          </div>
        )}

        <div className="ot-footer">
          <div className="ot-stats">
            <span className="ot-stat">
              <Heart size={13} />
              <span className="ot-stat-num">{displayLikes}</span>
            </span>
            <span className="ot-stat">
              <Eye size={13} />
              <span className="ot-stat-num">{displayViews}</span>
            </span>
          </div>
          {vibe && <span className="ot-vibe">{formatTag(vibe)}</span>}
        </div>

      </div>
    </article>
  );
};

export default Outlet;